import {useState, useCallback} from 'react'
import {toast} from 'react-toastify'
import {validateAddressAndTZ} from '../../AccountInfo/services/locationAPI'
import {
  getFieldFromErrorMessage,
  validateLocation,
} from '../../AccountInfo/utils/formHelpers'
import {buildFieldConfigMap} from '../../AccountInfo/config/locationFieldRules'
import {useFlags} from 'launchdarkly-react-client-sdk'

export function useAddressValidation(client, run) {
  const {frontendOverrideSst} = useFlags()

  const [isValidatingAddress, setIsValidatingAddress] = useState(false)

  const validateAddress = useCallback(
    async values => {
      // Run full required-field validation using the same fieldConfigMap
      // that LocationForm and useAccountTree use — so the required set
      // is always driven by LOCATION_FIELDS, not a separate hardcoded list.
      const fieldConfigMap = buildFieldConfigMap({
        account: {values},
        values,
        selectedCountry: values?.country,
        selectedTransactionType:
          values?.type === 'parent' ? 'single-location' : 'multiple-locations',
        states: [],
        frontendOverrideSst,
      })

      const fieldErrors = validateLocation(values, fieldConfigMap)

      if (Object.keys(fieldErrors).length > 0) {
        toast.error(Object.values(fieldErrors).join(', '))
        return {success: false, fieldErrors}
      }

      // All required fields present — hit the API
      setIsValidatingAddress(true)
      try {
        const response = await validateAddressAndTZ(run, client, {
          address: values.address,
          city: values.city,
          state: values.state,
          zipCode: values.postCode,
          country: values?.country,
        })

        const {timeZone, status, original, recommended, message} = response

        if (status !== 'SUGGESTED') {
          return {success: true, timeZone, values}
        }

        // Google's recommendation is identical to what the rep entered —
        // nothing to confirm, so treat it as verified and skip the suggestion.
        const norm = v =>
          String(v ?? '')
            .trim()
            .toLowerCase()
        const sameAddress =
          original &&
          recommended &&
          ['address', 'city', 'state', 'zipCode'].every(
            k => norm(original[k]) === norm(recommended[k]),
          )
        if (sameAddress) {
          return {success: true, timeZone, values}
        }

        // Address is plausible but not confirmed — surface it as a soft
        // failure (not a caught error) so callers reveal the editable form
        // with an inline entered-vs-recommended banner, rather than blocking
        // on a separate confirm dialog. timeZone is carried along so that
        // accepting the recommendation can save immediately instead of
        // re-running address validation on values Google already resolved
        // this lat/lng and timeZone from.
        return {
          success: false,
          fieldErrors: {},
          suggestion: {
            original,
            recommended: {...recommended, lat: response.lat, lng: response.lng},
            message,
            timeZone,
          },
        }
      } catch (error) {
        const message = error?.message || 'Address validation failed'
        const status = error?.status
        toast.error(message)
        const field = status === 422 ? getFieldFromErrorMessage(message) : null
        return {
          success: false,
          fieldErrors: field ? {[field]: message} : {},
        }
      } finally {
        setIsValidatingAddress(false)
      }
    },
    [client, run],
  )

  return {
    validateAddress,
    isValidatingAddress,
  }
}
